import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Share2, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api, formatNok } from "../lib/api";
import ShopLayout, { AVAIL_STYLE, SPEC_LABELS } from "../components/shop/ShopLayout";
import Gallery from "../components/shop/Gallery";
import PurchaseForm from "../components/shop/PurchaseForm";

export default function ProductPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(undefined);

  useEffect(() => {
    setProduct(undefined);
    window.scrollTo({ top: 0, behavior: "instant" });
    api.get(`/shop/products/${encodeURIComponent(slug)}`).then((r) => {
      setProduct(r.data);
      document.title = `${r.data.name} – MobilHub Butikk`;
    }).catch(() => setProduct(null));
  }, [slug]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: product.name, url });
      else { await navigator.clipboard.writeText(url); toast.success("Lenke kopiert"); }
    } catch { /* cancelled */ }
  };

  const scrollToForm = () => document.querySelector("#kjop")?.scrollIntoView({ behavior: "smooth" });

  return (
    <ShopLayout>
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-10">
        <Link to="/butikk" className="inline-flex items-center gap-1.5 text-sm font-medium text-electric hover:underline" data-testid="product-back-link">
          <ArrowLeft className="h-4 w-4" /> Tilbake til butikken
        </Link>

        {product === undefined && <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-electric" /></div>}

        {product === null && (
          <div className="mt-10 rounded-2xl border border-hairline bg-white p-10 text-center" data-testid="product-not-found">
            <p className="font-heading text-xl font-bold text-navy">Produktet finnes ikke lenger</p>
            <p className="mt-2 text-sm text-ink/60">Det kan være solgt eller fjernet fra butikken.</p>
          </div>
        )}

        {product && (
          <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12" data-testid="product-page">
            <Gallery images={product.images} name={product.name} />

            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-ink/50">{product.category}</p>
              <h1 className="mt-1 font-heading text-2xl font-bold text-navy sm:text-3xl" data-testid="product-name">{product.name}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <span className="font-heading text-2xl font-bold text-electric" data-testid="product-price">{formatNok(product.price)}</span>
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${AVAIL_STYLE[product.availability] || "bg-slate-100"}`} data-testid="product-availability">{product.availability}</span>
                {product.condition && <span className="rounded-full bg-offwhite px-2.5 py-1 text-xs font-medium text-navy" data-testid="product-condition">Tilstand: {product.condition}</span>}
              </div>

              <div className="mt-5 flex flex-col gap-2 sm:flex-row">
                <button onClick={scrollToForm} className="inline-flex items-center justify-center gap-2 rounded-full bg-warm px-6 py-3 text-sm font-semibold text-navy transition-colors hover:bg-warm-dark" data-testid="product-request-button">
                  <ShoppingBag className="h-4 w-4" /> Send kjøpsforespørsel
                </button>
                <button onClick={share} className="inline-flex items-center justify-center gap-2 rounded-full border border-hairline bg-white px-5 py-3 text-sm font-semibold text-navy transition-colors hover:border-electric" data-testid="product-share-button">
                  <Share2 className="h-4 w-4" /> Del
                </button>
              </div>
              <p className="mt-3 text-xs text-ink/50">Send en kjøpsforespørsel, så tar vi kontakt for å bekrefte tilgjengelighet og avtale kjøpet.</p>

              {product.description && (
                <div className="mt-6">
                  <h2 className="font-heading text-base font-bold text-navy md:text-lg">Beskrivelse</h2>
                  <p className="mt-2 whitespace-pre-line text-sm text-ink/75 leading-relaxed" data-testid="product-description">{product.description}</p>
                </div>
              )}

              {Object.entries(product.specs || {}).some(([, v]) => v) && (
                <div className="mt-6">
                  <h2 className="font-heading text-base font-bold text-navy md:text-lg">Spesifikasjoner</h2>
                  <dl className="mt-2 divide-y divide-hairline rounded-xl border border-hairline bg-white" data-testid="product-specs">
                    {Object.entries(SPEC_LABELS).filter(([k]) => product.specs?.[k]).map(([k, label]) => (
                      <div key={k} className="grid grid-cols-[minmax(0,130px)_1fr] gap-3 px-4 py-2.5 text-sm">
                        <dt className="text-ink/50">{label}</dt>
                        <dd className="whitespace-pre-line font-medium text-navy" data-testid={`product-spec-${k}`}>{product.specs[k]}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}

              <div id="kjop" className="mt-8 scroll-mt-24">
                <PurchaseForm product={product} />
              </div>
            </div>
          </div>
        )}
      </div>
    </ShopLayout>
  );
}
